import styled, { css } from "styled-components";
import { Text } from "../Text/Text.js";
import { ITheme } from "../Theme/Theme.types.js";
import { BUTTON_BORDER_SIZE } from "./Button.constants.js";
import { ButtonProps, DefaultButtonProps } from "./Button.types.js";
import { borderGradient } from "./helpers/borderGradient.js";
import { borderGradientOnTransparentBackground } from "./helpers/borderGradientOnTransparentBackground.js";
import { getBorderRadius } from "./helpers/getBorderRadius.js";
import { getColor } from "./helpers/getColor.js";
import { getFocusRing } from "./helpers/getFocusRing.js";
import { getGap } from "./helpers/getGap.js";
import { getPadding } from "./helpers/getPadding.js";
import { getShadow } from "./helpers/getShadow.js";
import { textGradient } from "./helpers/textGradient.js";

export const ChildrenWrapper = styled(Text)<{ visible: boolean }>`
    opacity: ${(props) => (props.visible ? 1 : 0)};
`;

const getDefaultStyle = ({ size, variant, color }: Pick<DefaultButtonProps, "size" | "variant" | "color">) => {
    if (variant === "fill")
        return css`
            box-shadow: ${(props) => getShadow({ color, ...props })};
            background: ${(props) => getColor({ color, value: "100", ...props })};
            color: white;

            ${color === "gradient" || color === "blueMonday" || color === "blackThirstday"
                ? borderGradient({ color })
                : css`
                      border: ${BUTTON_BORDER_SIZE} solid ${(props) => getColor({ color, value: "100", ...props })};
                  `}
        `;
    if (variant === "outline")
        return css`
            box-shadow: none;
            background: transparent;

            ${color === "gradient" || color === "blueMonday"
                ? css`
                      ${borderGradientOnTransparentBackground({ color, size })}

                      & ${ChildrenWrapper} {
                          ${textGradient({ color })}
                      }
                  `
                : css`
                      border: ${BUTTON_BORDER_SIZE} solid ${(props) => getColor({ color, value: color === "neutral" ? "50" : "100", ...props })};
                      color: ${(props) => getColor({ color, value: "100", ...props })};
                  `};
        `;
    if (variant === "tinted")
        return css`
            box-shadow: ${(props) => getShadow({ color, ...props })};
            border: ${BUTTON_BORDER_SIZE} solid ${(props) => getColor({ color, value: color === "neutral" ? "20" : "10", ...props })};

            ${color === "gradient" || color === "blueMonday"
                ? css`
                      border: none;

                      background: ${(props) => getColor({ color, value: "10", ...props })};
                      & ${ChildrenWrapper} {
                          ${textGradient({ color })}
                      }
                  `
                : css`
                      background: ${(props) => getColor({ color, value: color === "neutral" ? "20" : "10", ...props })};
                      color: ${(props) => getColor({ color, value: "100", ...props })};
                  `};
        `;
    return css`
        box-shadow: none;
        background: transparent;
        border: ${BUTTON_BORDER_SIZE} solid transparent;

        ${color === "gradient" || color === "blueMonday"
            ? css`
                  ${ChildrenWrapper} {
                      ${textGradient({ color })}
                  }
              `
            : css`
                  color: ${(props) => getColor({ color, value: color === "neutral" ? "90" : "100", ...props })};
              `};
    `;
};

const getHoveredStyle = ({ variant, color }: Pick<DefaultButtonProps, "variant" | "color">) => {
    if (variant === "ghost")
        return css`
            color: ${(props) => getColor({ color, value: color === "neutral" ? "100" : "110", ...props })};
        `;
    if (variant === "outline")
        return css`
            background-color: ${(props) => props.theme.grayscale.white};
        `;
    if (variant === "fill" && color === "blueMonday") {
        return css`
            background: ${(props) => props.theme.colors.primary[100]};
            &:before {
                background: ${(props) => props.theme.colors.primary[100]};
            }
        `;
    }

    return css`
        box-shadow: ${(props) => getShadow({ color, hover: true, ...props })};
    `;
};

const getFocusedStyle = ({ variant, color, theme }: Pick<DefaultButtonProps, "variant" | "color"> & { theme: ITheme }) => {
    if (variant === "fill" || variant === "tinted") return getFocusRing({ color, theme });
    if (variant === "outline")
        return css`
            background: ${(props) => getColor({ color, value: color === "neutral" ? "20" : "10", ...props })};
            ${getFocusRing({ color, theme })}
        `;
    return css`
        color: ${(props) => getColor({ color, value: color === "neutral" ? "100" : "110", ...props })};
    `;
};

const getDisabledStyle = ({ variant }: Pick<DefaultButtonProps, "variant">) => {
    if (variant === "fill" || variant === "tinted")
        return css`
            box-shadow: none;
            color: white;
            background: ${(props) => props.theme.grayscale[50]};
            border-color: ${(props) => props.theme.grayscale[50]};

            &:before {
                background: ${(props) => props.theme.grayscale[50]};
            }
        `;
    if (variant === "outline")
        return css`
            box-shadow: none;
            border-color: ${(props) => props.theme.grayscale[70]};
            color: ${(props) => props.theme.grayscale[70]};

            &:before {
                background: ${(props) => props.theme.grayscale[70]};
            }
        `;
    return css`
        box-shadow: none;
        color: ${(props) => props.theme.grayscale[50]};
    `;
};

export const StyledButton = styled.button<Omit<ButtonProps & DefaultButtonProps, "loading">>`
    position: relative;
    cursor: ${(props) => (props.disabledPointer ? "initial" : "pointer")};
    display: flex;
    justify-content: center;
    align-items: center;
    gap: ${(props) => getGap(props)};
    border-radius: ${(props) => getBorderRadius(props)};
    padding: ${(props) => getPadding(props)};
    ${(props) => props.width && `width: ${props.width};`}
    ${(props) => getDefaultStyle(props)}
    transition: all 0.3s ease;

    &:hover:not(:disabled):not(:focus) {
        ${(props) => getHoveredStyle(props)}
    }

    &:focus {
        transition: none;
        outline: none;
        ${(props) => getFocusedStyle(props)}
    }

    &:disabled {
        ${(props) => getDisabledStyle(props)}
        cursor: initial;
    }
`;

export const LoaderWrapper = styled.div`
    display: flex;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
`;
